import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AbcCore } from '@barthauer/abc/core';
import { ReadObjectTypeInfoModel } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ObjectsSetsComponent } from 'libs/abc/ui-frameworks/objects-sets/src/lib/objects-sets.component';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { GroupSelectionService } from '../group-selection.service';
import { ObjectSelectionComponent } from '../object-selection/object-selection.component';
import { ObjectSelectionGroup } from './group-selection.model';

@Component({
  selector: 'abc-object-selection-group',
  templateUrl: './group-selection.component.html',
  styleUrls: ['./group-selection.component.scss'],
  providers: [{ provide: GroupSelectionService, deps: [ResourceManagerService] }],
  standalone: false,
})
export class GroupSelectionComponent implements OnInit, OnChanges {
  @ViewChild('objectsSets', { static: false, read: ViewContainerRef }) vcr: ViewContainerRef;
  @ViewChild('ObjectSelection', { static: false }) objectSelection: ObjectSelectionComponent;
  @Input() public groups!: ObjectSelectionGroup[];
  @Input() public isObjectSetVisible: boolean = true;
  @Output() itemsCount = new EventEmitter<number>();
  @Output() selectedItemsCount = new EventEmitter<number>();
  public get res(): ResourceSet {
    return this.groupSelectionService.res;
  }

  constructor(
    private abcCore: AbcCore,
    private groupSelectionService: GroupSelectionService,
  ) {
    this.groupSelectionService.groupComponent = this;
  }

  ngOnInit(): void {
    /**/
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      const change = changes[propName];

      switch (propName) {
        case 'groups': {
          const oldGroups = change.previousValue as ObjectSelectionGroup[];
          const newGroups = change.currentValue as ObjectSelectionGroup[];

          if (this.groupSelectionService && newGroups !== oldGroups) {
            this.groupSelectionService.objectSelectionComponents.clear();
          }

          break;
        }

        default:
          break;
      }
    }
  }

  getSafeGroupDesignation(group: ObjectSelectionGroup): Observable<string> {
    if (group.designation) {
      return of(group.designation);
    } else if (group.objectTypeNameOrQueryInfo && typeof group.objectTypeNameOrQueryInfo === 'string') {
      const otModel = new ReadObjectTypeInfoModel();
      otModel.ObjectTypeName = group.objectTypeNameOrQueryInfo;
      return this.abcCore.objectSpace
        .getObjectType(group.objectTypeNameOrQueryInfo)
        .pipe(map((otInfo) => otInfo.displayTextPlural ?? (group.objectTypeNameOrQueryInfo as string)));
    } else {
      return of(group.name);
    }
  }

  /**
   * Gets all selected rows with all fields, including calculated unbound columns.
   * @param groupName Name of the group
   * @returns An array of JSON objects with dataField names as keys.
   */
  public getSelectedGridRows(groupName: string, onlyDataFields?: string[]): any[] {
    let gridRows: any[] = [];

    const objSelectionControl = this.groupSelectionService.objectSelectionComponents.get(groupName);

    if (objSelectionControl) {
      gridRows = objSelectionControl.getSelectedGridRows(onlyDataFields);
    }
    return gridRows;
  }

  /**
   * Gets all selected rows with all data bound fields, excluding calculated unbound columns.
   * @param groupName Name of the group
   * @returns An array of JSON objects with dataField names as keys.
   */
  public getSelectedDataRows(groupName: string): any[] {
    let dataRows: any[] = [];

    const objSelectionControl = this.groupSelectionService.objectSelectionComponents.get(groupName);

    if (objSelectionControl) {
      dataRows = objSelectionControl.getSelectedDataRows();
    }
    return dataRows;
  }

  /**
   * Gets all key values (e.g. ids) of all selected rows, excluding calculated unbound columns.
   * @param groupName Name of the group
   * @returns An array of row key values.
   */
  public getSelectedKeys(groupName: string): string[] {
    let ids: string[] = [];

    const objSelectionControl = this.groupSelectionService.objectSelectionComponents.get(groupName);

    if (objSelectionControl) {
      ids = objSelectionControl.getSelectedKeys();
    }

    return ids;
  }

  public getObjectSelectionControl(groupName: string): ObjectSelectionComponent {
    return this.groupSelectionService.objectSelectionComponents.get(groupName);
  }

  countItems(itemsCount: number) {
    this.itemsCount.emit(itemsCount);
  }

  public onItemsCountChange(selectedItemsCount: number): void {
    this.selectedItemsCount.emit(selectedItemsCount);
  }

  public async showObjectsSets() {
    const coreDataEntities = this.objectSelection.objectSelectionGrid.instance.getDataSource()?.items();

    if (coreDataEntities?.length) {
      const s = this.vcr.createComponent(ObjectsSetsComponent);
      const osc: ObjectsSetsComponent = <ObjectsSetsComponent>s?.instance;

      if (osc) {
        const objectType = coreDataEntities[0]?.ObjectType;
        osc.parentObjectType = objectType;

        osc.sourceDataEntities = <ICoreDataEntity>() => {
          return {
            objectType: objectType,
            entities: coreDataEntities as ICoreDataEntity[],
            idExpression: (id) => this.groupSelectionService.getIdOrOtExprName(id) || 'Id',
            objectTypeExpression: (ot) => this.groupSelectionService.getIdOrOtExprName(ot) || 'ObjectType',
          };
        };
        osc.loadObjectsSetsWindow();
        osc.onSelectEntities = (object) => {
          this.objectSelection.selectRowsByKeys(object);
        };
      }
    }
  }
}
